<template>
  <div
    :class="DS_FILE_ITEM_CLASSES.Content"
    data-cy="ds-file-item__content"
  >
    <div
      :class="DS_FILE_ITEM_CLASSES.FileName"
      data-cy="ds-file-item__file-name"
    >
      {{ fileNameLabel }}
    </div>

    <div
      v-if="hasDetails"
      :class="DS_FILE_ITEM_CLASSES.Details"
      data-cy="ds-file-item__details"
    >
      <span
        v-for="detail in fileDetails"
        :key="detail.name"
        :class="DS_FILE_ITEM_CLASSES.Detail"
        data-cy="ds-file-item__detail"
      >
        {{ detail.value }}
      </span>
      <div
        v-if="props.author"
        :class="[DS_FILE_ITEM_CLASSES.Detail, DS_FILE_ITEM_CLASSES.Author]"
        data-cy="ds-file-item__author"
      >
        <span>{{ uploadedByText }}</span>
        <DSUser
          v-bind="props.author"
          :has-action="props.author.hasAction ?? false"
          :size="props.author.size ?? DS_USER_SIZE.Medium"
        />
      </div>
    </div>

    <div
      v-if="props.errorMessages.length"
      :class="DS_FILE_ITEM_CLASSES.Error"
      data-cy="ds-file-item__error"
      role="alert"
    >
      <DSFormFieldError
        v-for="(errorMessage, index) in props.errorMessages"
        :key="`${errorMessage}-${index}`"
        :error-message="errorMessage"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { DS_FILE_ITEM_CLASSES } from "@/src/design_system/data_display/file_item/constants";
import DSUser from "@/src/design_system/data_display/user/components/DSUser.vue";
import type { DSUserProps } from "@/src/design_system/data_display/user/types/props";
import { DS_USER_SIZE } from "@/src/design_system/data_display/user/types/size";
import DSFormFieldError from "@/src/design_system/form_elements/form_field_error/components/DSFormFieldError.vue";
import { formatFileNameWithVersion } from "@/src/design_system/data_display/file_item/utils/formatFileNameWithVersion";

interface DSFileItemContentProps {
  fileName: string;
  fileVersion?: string;
  fileUploadedDate?: string;
  fileType?: string;
  fileSize?: string;
  fileDescription?: string;
  author?: DSUserProps;
  errorMessages: string[];
}

const props = defineProps<DSFileItemContentProps>();
const uploadedByText = window.I18n.designSystem.fileItem.uploadedBy;

const fileNameLabel = computed(() => formatFileNameWithVersion(props.fileName, props.fileVersion));

const fileDetails = computed(() =>
  [
    { name: "uploaded-date", value: props.fileUploadedDate },
    { name: "type", value: props.fileType },
    { name: "size", value: props.fileSize },
    { name: "description", value: props.fileDescription },
  ]
    .map(detail => ({ ...detail, value: detail.value?.trim() }))
    .filter((detail): detail is { name: string; value: string } => Boolean(detail.value))
);
const hasDetails = computed(() => fileDetails.value.length > 0 || Boolean(props.author));
</script>
